import React, { useEffect, useMemo, useState } from "react";

import Button from "components/Button";
import Modal from "components/Modal";
import PaymentSpeedSelect from "components/PaymentSpeedInput/PaymentSpeedSelect";
import Select from "components/Select";
import { PERCENTAGE_LIST } from "constants/haulpay-broker";
import type { IOption } from "models/Form";
import {
  type PaymentSettingsValue,
  type EditablePaymentSettingsValue,
  getPaymentSpeedSummary,
  LOCKED_BROKER_SPLIT_OPTION
} from "features/main/HaulPay/Shared/paymentSettingsConfirmationFlow";

const getPaymentSpeedValue = (value?: number | string | null): string | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  if (typeof value === "string" && value.startsWith("ach_")) return value;
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return undefined;
  return numericValue <= 1 ? "ach_1_day" : `ach_${numericValue}_day`;
};

const LOCKED_BROKER_SPLIT =
  LOCKED_BROKER_SPLIT_OPTION ?? PERCENTAGE_LIST.find((p) => p.amount === 100) ?? PERCENTAGE_LIST[2];

const getBrokerSplitSummary = (value?: number | string | null): string => {
  const normalizedValue = Number(value ?? 0);

  if (Number.isNaN(normalizedValue)) return "0% broker split";

  return `${normalizedValue}% broker split`;
};

export default function ContractTypeChangeConfirmationModal({
  isOpen,
  closeModal,
  onConfirm,
  onCloseModal,
  currentValue,
  defaultValue,
  initialValue,
  hasFactoringCompany = false,
  warningMessage,
  fromContractType,
  toContractType
}: Readonly<{
  isOpen: boolean;
  closeModal: () => void;
  onConfirm?: (value: Required<EditablePaymentSettingsValue>) => void;
  onCloseModal?: () => void;
  currentValue?: PaymentSettingsValue;
  defaultValue?: PaymentSettingsValue;
  initialValue?: EditablePaymentSettingsValue;
  hasFactoringCompany?: boolean;
  factoringId?: string;
  warningMessage?: string;
  fromContractType?: string;
  toContractType?: string;
}>) {
  const [selectedCarrierPaymentSpeed, setSelectedCarrierPaymentSpeed] = useState<IOption | null>(
    initialValue?.carrierPaymentSpeed ?? null
  );
  const [selectedBrokersQRFee, setSelectedBrokersQRFee] = useState<IOption | null>(
    hasFactoringCompany ? LOCKED_BROKER_SPLIT : initialValue?.brokersQRFee ?? PERCENTAGE_LIST[0]
  );

  useEffect(() => {
    setSelectedCarrierPaymentSpeed(initialValue?.carrierPaymentSpeed ?? null);
  }, [initialValue?.carrierPaymentSpeed]);

  useEffect(() => {
    setSelectedBrokersQRFee(
      hasFactoringCompany ? LOCKED_BROKER_SPLIT : initialValue?.brokersQRFee ?? PERCENTAGE_LIST[0]
    );
  }, [hasFactoringCompany, initialValue?.brokersQRFee]);

  const paymentSpeedValue = useMemo(
    () => getPaymentSpeedValue(selectedCarrierPaymentSpeed?.value),
    [selectedCarrierPaymentSpeed]
  );

  const handleCancel = () => {
    onCloseModal?.();
    closeModal();
  };

  const handleConfirm = () => {
    if (!selectedCarrierPaymentSpeed) return;

    onConfirm?.({
      carrierPaymentSpeed: selectedCarrierPaymentSpeed,
      brokersQRFee: hasFactoringCompany
        ? LOCKED_BROKER_SPLIT
        : selectedBrokersQRFee ?? PERCENTAGE_LIST[0]
    });
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleCancel}
      size="sm"
      wrapperClass="!my-auto desktop:!min-h-fit px-4 desktop:px-6"
      dialogClass="!bg-white dark:!bg-card !border !border-gray-200 dark:!border-card !p-0 !max-h-[calc(100vh-2rem)] !overflow-y-auto rounded-2xl shadow-lg w-full"
      closeClass="!hidden">
      <div className="border-b border-gray-200 dark:border-white/10 px-4 py-4 desktop:px-6 desktop:py-5">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-white/70">
          Contract Type Change
        </p>
        <h2 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white desktop:text-xl">
          Update payment settings?
        </h2>
        {fromContractType && toContractType && (
          <p className="mt-1 text-sm text-gray-500 dark:text-white/70">
            {fromContractType} {"\u2192"} {toContractType}
          </p>
        )}
      </div>

      <div className="px-4 py-4 desktop:px-6 desktop:py-5">
        <p className="text-sm leading-relaxed text-gray-600 dark:text-white/75">
          You&apos;re changing the contract type for this funding request. Review and confirm the
          payment speed and broker split for this relationship.
        </p>

        <div className="mt-4 grid gap-3 desktop:grid-cols-2">
          <div className="rounded-xl bg-gray-100 dark:bg-black/20 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-white/70">
              Current Speed
            </p>
            <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
              {getPaymentSpeedSummary(currentValue?.payout_days)}
            </p>
            <p className="mt-1 text-sm text-gray-600 dark:text-white/75">
              {getBrokerSplitSummary(currentValue?.factoring_fee_split_rate)}
            </p>
          </div>

          <div className="rounded-xl bg-gray-100 dark:bg-black/20 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-white/70">
              Relationship Default
            </p>
            <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
              {getPaymentSpeedSummary(defaultValue?.payout_days)}
            </p>
            <p className="mt-1 text-sm text-gray-600 dark:text-white/75">
              {getBrokerSplitSummary(defaultValue?.factoring_fee_split_rate)}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <h6 className="mb-1.5 text-sm font-medium text-gray-900 dark:text-white">
            Payment speed
          </h6>
          <PaymentSpeedSelect
            name="contract-type-change-payment-speed"
            noLabel
            inputProps={{
              value: selectedCarrierPaymentSpeed,
              onChange: (value: IOption) => setSelectedCarrierPaymentSpeed(value),
              inputClass:
                "!min-h-10 !rounded-lg !border !border-gray-200 dark:!border-white/10 !bg-white dark:!bg-card !px-3 !text-sm !text-gray-900 dark:!text-white",
              dropdownContainerClass: "!bg-white dark:!bg-card",
              direction: "down"
            }}
          />
          <p className="mt-1.5 text-xs text-gray-500 dark:text-white/60">
            Defaults to broker profile quick pay settings.
          </p>
        </div>

        <div className="mt-4">
          <h6 className="mb-1.5 text-sm font-medium text-gray-900 dark:text-white">Broker split</h6>
          <Select
            options={PERCENTAGE_LIST}
            value={hasFactoringCompany ? LOCKED_BROKER_SPLIT : selectedBrokersQRFee}
            onChange={(value) => setSelectedBrokersQRFee(value)}
            disabled={hasFactoringCompany}
            inputClass="!min-h-10 !rounded-lg !border !border-gray-200 dark:!border-white/10 !bg-white dark:!bg-card !px-3 !text-sm !text-gray-900 dark:!text-white"
            dropdownContainerClass="!bg-white dark:!bg-card"
            direction="up"
            usePortal={false}
          />
          {hasFactoringCompany && (
            <p className="mt-1.5 text-xs text-warning">
              Carrier has a factoring company on file and the broker split is locked to 100%.
            </p>
          )}
        </div>

        {warningMessage && (
          <div className="mt-4 rounded-lg border border-warning bg-warning/20 px-3 py-2.5 text-xs leading-relaxed text-warning">
            {warningMessage}
          </div>
        )}
      </div>

      <div className="flex flex-col-reverse gap-2 border-t border-gray-200 dark:border-white/10 px-4 py-4 desktop:flex-row desktop:justify-end desktop:gap-3 desktop:px-6">
        <Button
          type="button"
          rounded
          variant="outline"
          onClick={handleCancel}
          className="!h-9 w-full text-sm desktop:w-auto desktop:min-w-[7rem] border-gray-300 dark:border-white/20 bg-transparent text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-white/5">
          Cancel
        </Button>
        <Button
          type="button"
          rounded
          onClick={handleConfirm}
          disabled={!paymentSpeedValue}
          className="!h-9 w-full text-sm desktop:w-auto desktop:min-w-[10rem] !bg-primary dark:!bg-white !text-white dark:!text-card hover:!bg-primary/90 dark:hover:!bg-white/90">
          Confirm changes
        </Button>
      </div>
    </Modal>
  );
}
